import { createContext, ReactNode, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { SessionTokenPayload, UserAuthentication } from "../types/User.ts";
import { safeLocalStorage } from "../utils/safeLocalStroage.ts";
import { useSWRConfig } from "swr";
import { unsubscribeChat } from "../fetch/subscribe.fetch.ts";

// Define the types for the context value
interface AuthContextType {
  auth: UserAuthentication;
  startAuth: (token: string) => boolean;
  logout: () => Promise<void>;
}

// Create the context with default values
export const AuthContext = createContext<AuthContextType>({
  auth: { ok: false },
  startAuth: () => {
    return false;
  },
  logout: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  //initialize the session
  //TODO: Handle cases when the local storage is sick
  const { mutate } = useSWRConfig();
  const storedSession = safeLocalStorage.getItem("authToken");
  let init: UserAuthentication = { ok: false };
  try {
    init = storedSession ? JSON.parse(storedSession) : { ok: false };
  } catch (e) {
    console.error(e);
  }
  const [authToken, setAuthToken] = useState<UserAuthentication>(init);
  const startAuth = (token: string) => {
    try {
      const payload = jwtDecode(token) as SessionTokenPayload;
      const sessionToken = {
        ok: true,
        token: token,
        sub: payload.sub,
      };
      setAuthToken(sessionToken);
      safeLocalStorage.setItem("authToken", JSON.stringify(sessionToken));
      mutate(() => true);
      return true;
    } catch (e) {
      console.error(e);
      logout();
      return false;
    }
  };

  const logout = async () => {
    await unsubscribeChat();
    safeLocalStorage.removeItem("authToken"); // Clear token
    safeLocalStorage.removeItem("sessionToken"); // Clear token
    setAuthToken({ ok: false });
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider
      value={{
        auth: authToken,
        startAuth: startAuth,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
